import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderOption } from '../../api/types';
import * as S from './BookmarkFilters.styled';
import { BookmarkOrderSelect } from './BookmarkOrderSelect';
import { CategoriesLoader } from './CategoriesLoader';
import {
  DEFAULT_ID,
  SCROLL_STEP,
  SCROLL_GAP,
  SCROLLER_OFFSET,
  SCROLL_THRESHOLD,
} from './BookmarkFilters.constants';
import { debounce } from '@/utils/debounce';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { BookmarkedCategory } from '@/api/types/user.types';
import { ChevronDown, SortIcon } from '@/components/UI/Icons/Icons';

interface BookmarkFiltersProps {
  loading: boolean;
  orderBy: OrderOption;
  totalAdsCount?: number;
  categories: BookmarkedCategory[];
  onOrderChange: (order: OrderOption) => void;
  onCategoryChange: (categoryId: string | null) => void;
}

export const BookmarkFilters = ({
  loading,
  orderBy,
  categories,
  totalAdsCount,
  onOrderChange,
  onCategoryChange,
}: BookmarkFiltersProps) => {
  const { t } = useTranslation();
  const { isDesktop } = useBreakpoint();
  const orderButtonRef = useRef<HTMLButtonElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [active, setActive] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(true);
  const [isSortingDropdownOpen, setIsSortingDropdownOpen] = useState(false);

  const allLabel = isDesktop
    ? 'bookmark_collections.default_category'
    : 'bookmark_collections.default_category_mobile';
  const hasCategories = categories.length > 0;
  const hasScroller = !loading && isDesktop && isOverflowing;
  const orderButtonWidth = orderButtonRef.current?.offsetWidth ?? 0;
  const scrollerOffset = orderButtonWidth + SCROLLER_OFFSET;

  const sortedCategories = useMemo(() => {
    return [...categories].sort(
      (a, b) => b.bookmarkedAdsCount - a.bookmarkedAdsCount
    );
  }, [categories]);

  const handleScrollerClick = () => {
    const container = scrollContainerRef.current;

    if (!container) return;

    const scrollLeft = container.scrollLeft + SCROLL_STEP;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;

    if (!isScrolled) {
      if (scrollLeft < maxScrollLeft) {
        const isEnd = scrollLeft > maxScrollLeft - SCROLL_THRESHOLD;

        if (isEnd) {
          container.scrollBy({ left: maxScrollLeft, behavior: 'smooth' });
          setIsScrolled(true);
        } else {
          container.scrollBy({ left: SCROLL_STEP, behavior: 'smooth' });
        }
      } else {
        container.scrollBy({
          behavior: 'smooth',
          left: maxScrollLeft - container.scrollLeft,
        });
        setIsScrolled(true);
      }

      return;
    }

    if (container.scrollLeft - SCROLL_STEP > SCROLL_THRESHOLD) {
      container.scrollBy({ left: -SCROLL_STEP, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: -container.scrollLeft, behavior: 'smooth' });
      setIsScrolled(false);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;

    if (!container) return;

    const handleScroll = () => {
      if (
        container.scrollLeft + SCROLL_GAP >=
        container.scrollWidth - container.clientWidth
      ) {
        container.scrollBy({ left: container.scrollLeft });
        setIsScrolled(true);
        return;
      }

      if (container.scrollLeft - SCROLL_GAP <= 0) {
        container.scrollBy({ left: 0 });
        setIsScrolled(false);
      }
    };

    const checkOverflow = () => {
      const overflowPx = container.scrollWidth - container.clientWidth;
      setIsOverflowing(overflowPx > SCROLL_THRESHOLD);
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);

    resizeObserver.observe(container);
    container.addEventListener('scroll', debounce(handleScroll, 100));

    return () => {
      resizeObserver.disconnect();
      container.removeEventListener('scroll', debounce(handleScroll, 100));
    };
  }, [categories, isDesktop]);

  const handleCategoryClick = (item?: BookmarkedCategory) => {
    const categoryId = item?.category?.id ?? '';

    setActive(categoryId);
    onCategoryChange(categoryId);

    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const dataId = categoryId || DEFAULT_ID;
      const activeElement = container.querySelector(`[data-id="${dataId}"]`);

      if (activeElement) {
        activeElement.scrollIntoView({
          block: 'nearest',
          inline: 'center',
          behavior: 'smooth',
        });
      }
    }
  };

  const handleOrderChange = (order: OrderOption) => {
    onOrderChange(order);
    setIsSortingDropdownOpen(false);
  };

  const renderDropdownButton = () => {
    if (!(loading || hasCategories)) return null;

    return (
      <S.OrderButton
        ref={orderButtonRef}
        $isMinimized={!isDesktop}
        $isActive={isSortingDropdownOpen}
        onClick={() => setIsSortingDropdownOpen(true)}
      >
        <SortIcon />
        {isDesktop && (
          <>
            {t('bookmark_collections.sort_button_title')}
            <S.DropdownIcon />
          </>
        )}
        <BookmarkOrderSelect
          value={orderBy}
          isOpen={isSortingDropdownOpen}
          onChange={handleOrderChange}
          onClose={() => setIsSortingDropdownOpen(false)}
        />
      </S.OrderButton>
    );
  };

  if (!loading && !categories.length) return null;

  return (
    <S.Container>
      {hasScroller && isScrolled && (
        <S.ScrollerButton $isReversed onClick={handleScrollerClick}>
          <ChevronDown />
        </S.ScrollerButton>
      )}

      <S.CategoriesList ref={scrollContainerRef}>
        {loading ? (
          <CategoriesLoader />
        ) : (
          <>
            {hasCategories && (
              <S.CategoryItem
                $isActive={!active}
                data-id={DEFAULT_ID}
                onClick={() => handleCategoryClick()}
              >
                {t(allLabel, { count: totalAdsCount })}
              </S.CategoryItem>
            )}

            {sortedCategories.map(item => {
              const isActive = active === item.category.id;

              return (
                <S.CategoryItem
                  $isActive={isActive}
                  key={item.category.id}
                  data-id={item.category.id}
                  onClick={() => handleCategoryClick(item)}
                >
                  {item.category.name} {item.bookmarkedAdsCount}
                </S.CategoryItem>
              );
            })}
          </>
        )}
      </S.CategoriesList>

      {hasScroller && !isScrolled && (
        <S.ScrollerButton
          $rightOffset={scrollerOffset}
          onClick={handleScrollerClick}
        >
          <ChevronDown />
        </S.ScrollerButton>
      )}

      {renderDropdownButton()}
    </S.Container>
  );
};
